import { useState } from 'react';
import { updateEvaluationMethodThresholds } from './risksApi';
import type { EvaluationMethod } from './types';

interface EvaluationThresholdsPanelProps {
  methods: EvaluationMethod[];
  onUpdated?: () => void;
}

export function EvaluationThresholdsPanel({
  methods,
  onUpdated,
}: EvaluationThresholdsPanelProps) {
  const [methodId, setMethodId] = useState(methods[0]?.id ?? '');
  const [thresholdsJson, setThresholdsJson] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const selected = methods.find((m) => m.id === methodId);

  const loadMethod = (id: string) => {
    setMethodId(id);
    const method = methods.find((m) => m.id === id);
    setThresholdsJson(method ? JSON.stringify(method.thresholds, null, 2) : '');
    setMessage(null);
  };

  if (methods.length === 0) return null;

  const handleSave = async () => {
    if (!methodId) return;
    setSaving(true);
    setMessage(null);
    try {
      const parsed = JSON.parse(thresholdsJson) as { maxValue: number; label: string; color: string }[];
      if (!Array.isArray(parsed) || parsed.length === 0) {
        setMessage('Umbrales inválidos: debe ser un array no vacío');
        return;
      }
      await updateEvaluationMethodThresholds(methodId, parsed);
      setMessage('Umbrales actualizados. Recálculo EVA-05 en curso.');
      onUpdated?.();
    } catch {
      setMessage('JSON inválido o error al guardar umbrales');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className='rounded-lg border p-4 bg-card space-y-3'>
      <h2 className='text-sm font-semibold'>Umbrales de evaluación (MEV-04)</h2>
      <div className='flex flex-wrap gap-2 items-center'>
        <select
          value={methodId}
          onChange={(e) => loadMethod(e.target.value)}
          className='rounded-md border px-3 py-2 text-sm'
        >
          {methods.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select>
        <button
          type='button'
          disabled={saving || !selected}
          onClick={() => void handleSave()}
          className='px-3 py-2 text-sm rounded-md bg-primary text-primary-foreground disabled:opacity-50'
        >
          {saving ? 'Guardando…' : 'Guardar y recalcular'}
        </button>
      </div>
      <textarea
        value={thresholdsJson || (selected ? JSON.stringify(selected.thresholds, null, 2) : '')}
        onChange={(e) => setThresholdsJson(e.target.value)}
        rows={6}
        className='w-full font-mono text-xs rounded-md border p-2'
      />
      {message && <p className='text-sm text-muted-foreground'>{message}</p>}
    </section>
  );
}
